'use client'

import { useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import {
  EngineProvider,
  FloorplanView,
  SceneItemsMesh,
  type AssetMeshSource,
} from '@ideight/engine/react'
import { defaultLightingRig } from '@ideight/engine/view'
import type { Floorplan } from '@ideight/engine/model'

/**
 * Read-only 3D viewer for a floorplan + its placed furniture. Shared by
 * the public landing demo (`/preview`) and the sample gallery
 * (`/samples/[id]`). Caller hydrates the `Floorplan` and supplies the
 * public catalogue `assetsById` (asset GLBs are public-read).
 */
export function SampleScene({
  floorplan,
  assetsById,
}: {
  readonly floorplan: Floorplan
  readonly assetsById: ReadonlyMap<string, AssetMeshSource>
}) {
  const lights = useMemo(() => defaultLightingRig(), [])

  const corners = floorplan.getCorners()
  const xs = corners.map((c) => c.x)
  const ys = corners.map((c) => c.y)
  const cx = corners.length > 0 ? (Math.min(...xs) + Math.max(...xs)) / 2 / 1000 : 0
  const cz = corners.length > 0 ? -((Math.min(...ys) + Math.max(...ys)) / 2) / 1000 : 0

  return (
    <Canvas
      shadows
      camera={{ position: [cx + 8, 7, cz + 8], fov: 45 }}
      data-testid="preview-canvas"
      onCreated={({ scene, gl, camera }) => {
        const w = window as unknown as Record<string, unknown>
        w.__IDEIGHT_SCENE__ = scene
        w.__IDEIGHT_GL__ = gl
        w.__IDEIGHT_CAMERA__ = camera
      }}
    >
      {lights.map((light, i) => (
        <primitive key={i} object={light} />
      ))}
      <OrbitControls target={[cx, 0, cz]} makeDefault />
      <EngineProvider floorplan={floorplan}>
        <FloorplanView />
        <SceneItemsMesh assetsById={assetsById} />
      </EngineProvider>
    </Canvas>
  )
}
